/**
 * Camada fina sobre o gtag.js (Google tag GT-... + Google Ads AW-...).
 * Consent Mode v2: o default "denied" está no index.html; aqui a gente
 * atualiza quando a pessoa aceita/recusa no banner.
 */
import { attributionParams } from "./attribution";

export const GOOGLE_TAG_ID = import.meta.env.VITE_GOOGLE_TAG_ID || "";
export const ADS_CONVERSION_ID = import.meta.env.VITE_ADS_CONVERSION_ID || "";
/** Rótulo completo da ação de conversão do Ads, ex.: "AW-11265841748/AbC-dEf123". */
export const ADS_LEAD_LABEL = import.meta.env.VITE_ADS_LEAD_LABEL || "";

const CONSENT_KEY = "ci_consent"; // "granted" | "denied"

type GtagArgs = [string, ...unknown[]];
declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export function gtag(...args: GtagArgs): void {
  if (typeof window === "undefined") return;
  // O gtag.js só processa objetos `arguments` no dataLayer (não arrays),
  // então delega ao gtag() definido no snippet do index.html.
  if (typeof window.gtag === "function") {
    window.gtag(...args);
    return;
  }
  window.dataLayer = window.dataLayer || [];
  (function (..._a: unknown[]) {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  })(...args);
}

export function getConsent(): "granted" | "denied" | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    return null;
  }
}

export function setConsent(state: "granted" | "denied"): void {
  try {
    localStorage.setItem(CONSENT_KEY, state);
  } catch {
    /* ignore */
  }
  const value = state === "granted" ? "granted" : "denied";
  gtag("consent", "update", {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
  });
  if (state === "granted") {
    gtag("event", "consent_granted", {});
    // page_view que pode ter sido segurado pelo wait_for_update
    trackPageView(window.location.pathname + window.location.search);
  }
}

/** Reaplica uma escolha já salva (chamado no boot, antes de carregar o gtag). */
export function applyStoredConsent(): void {
  const stored = getConsent();
  if (stored) {
    const value = stored === "granted" ? "granted" : "denied";
    gtag("consent", "update", {
      ad_storage: value,
      ad_user_data: value,
      ad_personalization: value,
      analytics_storage: value,
    });
  }
}

export function trackPageView(path: string): void {
  if (!GOOGLE_TAG_ID) return;
  gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: document.title,
  });
}

/** Evento genérico. */
export function track(event: string, params: Record<string, unknown> = {}): void {
  gtag("event", event, params);
}

/**
 * Lead — a pessoa iniciou contato (WhatsApp, telefone, formulário).
 * Dispara GA4 `generate_lead` + conversão do Ads (se o rótulo estiver configurado).
 */
export function trackLead(input: {
  channel: "whatsapp" | "phone" | "form" | "chat";
  location?: string; // de onde no site (ex.: "planos", "hero", "rodapé")
  plan?: string;
  value?: number;
  /** Produto da página de origem (internet, telefonia, cameras...). "geral" fora de página de produto. */
  productLine?: string;
}): void {
  const params: Record<string, unknown> = {
    method: input.channel,
    contact_location: input.location || "",
    product_line: input.productLine || "geral",
    ...(input.plan ? { plan: input.plan } : {}),
    ...attributionParams(),
  };
  track("generate_lead", params);

  if (ADS_LEAD_LABEL) {
    gtag("event", "conversion", {
      send_to: ADS_LEAD_LABEL,
      value: input.value ?? 0,
      currency: "BRL",
    });
  }
}
